import { useEffect, useState } from 'react';

import { CircularProgress, SvgIconProps, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Check, EventAvailable, LocationOn } from '@mui/icons-material';
import { Card, VolunteerActivismIcon } from '@uplift/icons';

import { StepContainer, StepContent, StepHeading, WaveBg } from '../../components';
import { MATCHES_LOADING_DURATION } from '../machine/utils';
import { CareType } from '@uplift/api';

export type MatchesLoadingProps = {
  hasInsurance: boolean;
  steps?: Step[];
  title?: string;
  careType?: CareType;
};

type Step = {
  title: string;
  icon: React.ElementType<SvgIconProps>;
  hidden?: boolean;
};

export const MatchesLoading: React.FC<MatchesLoadingProps> = (props) => {
  const { classes } = useMatchesLoadingStyles();

  const steps: Step[] = (props.steps ?? [
    {
      title: 'Meet your preferences',
      icon: LocationOn,
    },
    {
      title: 'Accept your insurance',
      icon: Card,
      hidden: !props.hasInsurance
    },
    {
      title: 'Have upcoming availability',
      icon: EventAvailable,
    },
    {
      title: 'Match your needs',
      icon: VolunteerActivismIcon,
    }
  ]).filter(step => !step.hidden);

  /**
   * Calculate the duration of each step based on the total duration and the number of steps
   * We subtract 500ms from the total duration to allow for the last step completion to be visible
  */
  const stepsCount = steps.length;
  const stepDuration = (MATCHES_LOADING_DURATION - 500) / stepsCount;

  const title = [ CareType.therapy, CareType.therapyAndPsychiatry ].includes(props.careType)
    ? 'Identifying your best therapist matches who...'
    : 'Gathering your best providers who';

  return (
    <StepContainer>
      <WaveBg color="secondary" headerClass={classes.waveHeader} />
      <StepContent>
        <StepHeading align="center">{props.title ?? title}</StepHeading>
        {steps.map((step, index) => {
          return (
            <LoadingStep
              key={index}
              delay={index * stepDuration}
              duration={stepDuration}
              icon={step.icon}
              title={step.title}
            />
          );
        })}
      </StepContent>
    </StepContainer>
  );
};

type LoadingStepProps = {
  delay: number;
  duration: number;
  icon: React.ElementType<SvgIconProps>;
  title: string;
};

const LoadingStep: React.FC<LoadingStepProps> = ({ delay, icon: Icon, title, duration }) => {
  const { classes, cx } = useLoadingStepStyles();
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isLoaded, setIsLoaded ] = useState(false);
  const isLoadingOrLoaded = isLoading || isLoaded;

  useEffect(() => {
    let loadTimer: NodeJS.Timeout;

    const delayTimer = setTimeout(() => {
      setIsLoading(true);
      loadTimer = setTimeout(() => {

        setIsLoading(false);
        setIsLoaded(true);

      }, duration);

      return () => clearTimeout(loadTimer);
    }, delay);

    return () => clearTimeout(delayTimer);
  }, [ delay ]);

  return (
    <div className={cx(classes.step, { [classes.stepLoaded]: isLoaded })}>
      <div className={cx(classes.iconContainer, { [classes.iconContainerLoading]: isLoading, [classes.iconContainerLoaded]: isLoaded })}>
        <Icon color="inherit" className={classes.icon} />
      </div>
      <Typography variant="body1" className={cx(classes.title, { [classes.titleLoadingOrLoaded]: isLoadingOrLoaded })}>{title}</Typography>
      <div className={classes.progressIconContainer}>
        {isLoading && <CircularProgress className={classes.circularProgress} />}
      </div>
      <div className={classes.progressIconContainer}>
        <Check className={cx(classes.checkIcon, isLoaded && classes.checkIconLoaded)} />
      </div>
    </div>
  );
};

const useMatchesLoadingStyles = makeStyles({ name: 'MatchesLoading' })(theme => ({
  waveHeader: {
    height: '40px',
    backgroundColor: theme.palette.secondary.background,
  }
}));

const useLoadingStepStyles = makeStyles({ name: 'LoadingStep' })(theme => ({
  step: {
    '&:first-of-type': {
      marginTop: theme.spacing(4),
    },
    marginTop: theme.spacing(1),
    background: theme.palette.grey[50],
    borderRadius: '4px',
    border: `1px solid ${theme.palette.grey[50]}`,
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2),
    position: 'relative',

    transition: 'background 1s, border 1s, color 0.3s',
  },
  stepLoaded: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.success.border}`,
  },
  iconContainer: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.secondary,
    display: 'grid',
    placeItems: 'center',
  },
  iconContainerLoading: {
    background: theme.palette.secondary.background,
    color: theme.palette.secondary.main,
  },
  iconContainerLoaded: {
    background: theme.palette.success.background,
    color: theme.palette.success.main,
    transition: 'background 1s, color 0.3s',
  },
  icon: {
    fontSize: '18px',
  },
  title: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    flex: 1,
  },
  titleLoadingOrLoaded: {
    color: theme.palette.text.primary
  },

  progressIconContainer: {
    display: 'flex',
    position: 'absolute',
    right: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
  },
  circularProgress: {
    color: theme.palette.secondary.main,
    width: '24px !important',
    height: '24px !important',
  },
  checkIcon: {
    color: theme.palette.success.main,
    opacity: 0,
    transition: 'opacity 0.3s',
  },
  checkIconLoaded: {
    opacity: 1,
  }
}));
